import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import ElasticService from "src/services/elasticService";

import { createDeviceMdmCommand } from "@dashboard/devices/api";
import { useCurrentSubscriptionExpired, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { License } from "@devices/types";
import authService from "@services/authService";
import { createNvcSession } from "@services/vncService";
import { SecondaryButton } from "@shared/CustomButton";
import { showErrorToast } from "@shared/toasts/Toasts";

const getReasonMessage = (props: {
    isSubscriptionExpired: boolean;
    isVncEnabled: boolean;
    hasOnlineDevice: boolean;
    limit: number;
}) => {
    if (props.limit <= 0) {
        return i18next.t("common:section.logStreamer.streamLimitReached");
    }
    if (!props.hasOnlineDevice) {
        return i18next.t("sidepanels:devicePanel.tooltip.deviceOffline");
    }
    if (!props.isVncEnabled) {
        return i18next.t("sidepanels:devicePanel.tooltip.vncDisabled");
    }
    return i18next.t("sidepanels:devicePanel.tooltip.vncEnabled");
};

export const VncButton = (props: License) => {
    const [loadingState, setLoadingState] = useState({ createLoading: false });

    const { t } = useTranslation(["sidepanels"]);
    const user = authService.getCurrentUser();
    const hasOnlineDevice = !!props.device?.isOnline;
    const isVncEnabled =
        (props.device?.lastState?.services?.emteria?.vncr?.enabled ?? false) ||
        props.device?.lastState?.properties?.["persist.sys.vncr.enabled"] === "1";
    const limit = useWorkspaceFeature("StreamSessionLimit");
    const isSubscriptionExpired = useCurrentSubscriptionExpired();
    const hasScreenMirror = !isSubscriptionExpired && isVncEnabled && hasOnlineDevice && limit > 0;

    const onClick = async () => {
        if (!props.device?.id) {
            return;
        }
        try {
            setLoadingState({ createLoading: true });
            const response = await createNvcSession(props.device.id);
            const command = {
                command: "sendBroadcast",
                caption: "Start screen mirroring",
                intent: {
                    component: "com.emteria.vnc/.receivers.CommandReceiver",
                    action: "emteria.intent.action.START_REVERSE_VNC",
                    extras: [
                        { key: "host", type: "string", value: response.data.model.host },
                        {
                            key: "token",
                            type: "string",
                            value: response.data.model.token,
                        },
                    ],
                },
            };
            await createDeviceMdmCommand({ deviceId: props.device.id, json: command });
            window.open(
                `https://vnc.staging.emteria.com/vnc.html?autoconnect=true&resize=scale&token=${response.data.model.token}`,
                "_blank",
                "width=600,height=400,left=200,top=200",
            );
            if (user?.email) {
                ElasticService.createTransaction("vnc", {
                    device_id: props.device.id,
                    user_email: user.email,
                });
            }
        } catch (_error) {
            showErrorToast(t("sidepanels:devicePanel.toast.vncFailed"));
        } finally {
            setLoadingState({ createLoading: false });
        }
    };

    return (
        <SecondaryButton
            onClick={onClick}
            disabled={!hasScreenMirror || loadingState.createLoading}
            loading={loadingState.createLoading}
            tooltipProps={{
                title: getReasonMessage({ isSubscriptionExpired, isVncEnabled, hasOnlineDevice, limit }),
            }}
        >
            <i className="fa-regular fa-eye" />
        </SecondaryButton>
    );
};
